import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subject, takeUntil } from "rxjs";
import {
	AbstractModal,
	BroadcastService,
	ObjectUtils,
	ToasterType
} from "@cv-invision/core";
import { BlurShape, CanvasOptions, ShapeCoordinates } from "./model/shape.model";
import { ConfirmationService } from "primeng/api";
import { ViewsService } from '../../../../../services/views.service';
import { ExternalApiService } from '../../../../services/external-api.service';

@Component({
	selector: 'cv-invision-ux-draw-forms',
	templateUrl: './draw-forms.component.html',
	styleUrls: ['./draw-forms.component.scss'],
	providers: [ ConfirmationService ],
})
export class DrawFormsComponent extends AbstractModal implements OnInit, OnDestroy {
	@ViewChild('canvasImage') canvas: ElementRef<HTMLCanvasElement>;
	@ViewChild('canvasParentContainer') canvasParentContainer: ElementRef<HTMLElement>;
	private ctx: CanvasRenderingContext2D;
	private interval: number;
	private atComponentDestroy = new Subject();

	showLoading: boolean;
	previewPhoto: string;
	retriesLoadImage = 0;
	photoIsAvailable = true;
	canvasLoaded = false;
	currentAlbumImage: any;
	path: string;
	image: any;
	photos: any;
	order: any;
	newBlur: boolean;
	isDown: boolean;
	saving: boolean;
	loading: boolean;
	startX: number;
	startY: number;

	position: ShapeCoordinates;
	canvasOptions: CanvasOptions;
	canvasHeight: string;

	listBlur: BlurShape[] = [];
	oldBoundingBoxes = [];

	get listChanges(): boolean {
		return this.oldBoundingBoxes?.length === 0 && this.listBlur.length === 0;
	}

	constructor(
		private externalApiService: ExternalApiService,
		private confirmationService: ConfirmationService,
		private broadcastService: BroadcastService,
		private viewsService: ViewsService
		) {
		super();
		this.position = new ShapeCoordinates();
		this.canvasOptions = new CanvasOptions();
		this.saving = false;
		this.canvasHeight = '100%';
	}

	async ngOnInit(): Promise<void> {
		this.showLoading = true;
		this.isDown = false;
		this.newBlur = false;
		this.loading = true;

		if (this.data) {
			this.currentAlbumImage = this.data.currentAlbumImage;
			this.path = this.data.jsonPath;
			this.photos = this.data.photos;
			this.order = this.data.order;
			this.previewPhoto = this.currentAlbumImage.thumb;

			this.externalApiService
				.getAttachmentById(this.order.id, this.currentAlbumImage.id)
				.pipe(takeUntil(this.atComponentDestroy))
				.subscribe(attachment => {
				this.currentAlbumImage = attachment;
				this.currentAlbumImage.src = attachment.path;

				if(attachment.backUpS3Path && attachment.backUpS3Path != '') {
					this.currentAlbumImage.src = attachment.backUpS3Path;
				}

				this.showLoading = false;
				this.loadImage();
			});
		}
	}

	/**
	 * event when image is loaded into canvas
	 */
	loadImage() {
		this.image = new Image();
		this.image.crossOrigin = "anonymous";
		this.image.src = this.currentAlbumImage.src;
		this.image.onload = () => {
			this.canvas.nativeElement.height = window.innerHeight * 0.75;
			this.canvas.nativeElement.width = this.canvasParentContainer.nativeElement.clientWidth * 0.95;

			this.ctx = this.canvas.nativeElement.getContext('2d');
			const wrh = this.image.width / this.image.height;
			this.canvasOptions.width = this.canvas.nativeElement.width;
			this.canvasOptions.height = this.canvasOptions.width / wrh;

			if (this.canvasOptions.height > this.canvas.nativeElement.height) {
				this.canvasOptions.height = this.canvas.nativeElement.height;
				this.canvasOptions.width = (this.canvasOptions.height * wrh);
			}

			this.canvasHeight = (this.canvasOptions.height - 146) + 'px';

			this.canvas.nativeElement.width = this.canvasOptions.width;
			this.canvas.nativeElement.height = this.canvasOptions.height;

			const xOffset = this.canvasOptions.width < this.canvas.nativeElement.width ? ((this.canvas.nativeElement.width - this.canvasOptions.width) / 2) : 0;
			const yOffset = this.canvasOptions.height < this.canvas.nativeElement.height ? ((this.canvas.nativeElement.height - this.canvasOptions.height) / 2) : 0;

			this.ctx.drawImage(this.image, xOffset, yOffset, this.canvasOptions.width, this.canvasOptions.height);
			this.showLoading = false;
			this.canvasLoaded = true;

			const offset = this.canvas.nativeElement.getBoundingClientRect();
			this.canvasOptions.offsetX = offset.x;
			this.canvasOptions.offsetY = offset.y;

			this.canvasOptions.scrollX = this.canvas.nativeElement.scrollLeft;
			this.canvasOptions.scrollY = this.canvas.nativeElement.scrollTop;

			if(this.currentAlbumImage?.boundingBoxes) {
				this.oldBoundingBoxes = ObjectUtils.recursiveDeepCopy(this.currentAlbumImage.boundingBoxes);
				for (const shape of this.currentAlbumImage.boundingBoxes){
					const blur = new BlurShape();
					blur.left = this.canvasOptions.width *  shape.left;
					blur.top = this.canvasOptions.height * shape.top;
					blur.width = this.canvasOptions.width * shape.width;
					blur.height = this.canvasOptions.height * shape.height;
					this.listBlur.push(blur);
				}
			}

			this.drawShapes();
			this.loading = false;
		}
	}

	/**
	 * Callback after image is loaded
	 */
	onImageLoad(): void {
		this.showLoading = false;
		if (this.interval) {
			clearInterval(this.interval);
		}
	}

	/**
	 * Callback when the image has error during loading
	 */
	onImageError(): void {
		this.previewPhoto = null;
		if (this.interval) {
			clearInterval(this.interval);
		}

		this.showLoading = true;
		this.interval = window.setInterval(() => {
			this.previewPhoto = this.currentAlbumImage.thumb;
			this.retriesLoadImage++;

			if (this.retriesLoadImage >= 10) {
				this.photoIsAvailable = false;
				this.showLoading = false;
				clearInterval(this.interval);
			}
		}, 1000);
	}

	/**
	 * event to show & hide blur in image
	 * @param index
	 */
	showBlur(index: number): void {
		this.listBlur[index].visible = !this.listBlur[index].visible;
		this.clearCanvas();
		this.drawShapes();
	}

	/**
	 * event to remove blur
	 * @param index
	 */
	removeBlur(index: number): void {
		this.listBlur.splice(index, 1);
		this.clearCanvas();
		this.drawShapes();
	}

	/**
	 * event to clear canvas to redraw shapes
	 */
	clearCanvas(): void {
		this.ctx.clearRect(0, 0, this.canvas.nativeElement.width, this.canvas.nativeElement.height);
		this.ctx.drawImage(this.image, 0, 0, this.canvasOptions.width, this.canvasOptions.height);
	}

	/**
	 * event to activate draw shapes
	 */
	addNewBlur(): void {
		this.newBlur = true;
	}

	/**
	 * event when a blur is selected
	 * @param index
	 * @param event
	 */
	onSelectBlur(index: any, event: any): void {
    for (const blur of this.listBlur) {
      blur.active = false;
    }
    this.listBlur[index].active = true;
		this.clearCanvas();
		this.drawShapes();
	}

	/**
	 * event to confirm the area to be blured in the image
	 */
	confirmBlur(): void {
		//console.log(this.position);
		//return;
		if(this.position.x != 0 && this.position.x  && this.position.x != 0 && this.position.y != 0) {
			this.newBlur = false;

			if(this.position.width < 0) {
				this.position.width = Math.abs(this.position.width);
				this.position.x = this.position.x - this.position.width;
			}

			if(this.position.height < 0) {
				this.position.height = Math.abs(this.position.height);
				this.position.y = this.position.y - this.position.height;
			}

			const newBlur = new BlurShape();
			newBlur.left = this.position.x;
			newBlur.top = this.position.y;
			newBlur.width = this.position.width;
			newBlur.height = this.position.height;

			const blurs = ObjectUtils.recursiveDeepCopy(this.listBlur)
			blurs.push(newBlur);

			this.listBlur = blurs;
		}else {
			this.broadcastService.broadcast({
				name: ToasterType.WARNING,
				event: { message: 'You have to drag a zone inside the image to create a blur' }
			});
		}
	}

	cancelBlur(): void {
		this.clearCanvas();
		this.drawShapes();
		this.newBlur = false;
	}

	/**
	 * event when click save button
	 */
	onSaveBlur(): void {
		this.saving = true;
		this.confirmationService.confirm({
			message: 'Are you sure that you want to proceed?',
			header: 'Save Blur',
			icon: 'pi pi-exclamation-triangle',
			accept: async () => {
				await this.saveBlur();
			},
			reject: () => {
				this.saving = false;
				this.loading = false;
			}
		});
	}

	/**
	 * event to call API to save blurs
	 */
	async saveBlur(): Promise<void> {
		const blursData = [];
		this.loading = true;

		for (const blurItem of this.listBlur) {
			const blur = {
				left: blurItem.left / this.canvasOptions.width,
				top: blurItem.top / this.canvasOptions.height,
				width: blurItem.width / this.canvasOptions.width,
				height: blurItem.height / this.canvasOptions.height
			}
			blursData.push(blur);
		}

		const updateAttachment = await this.externalApiService
			.saveBlurAttachmentById(this.order.id, this.currentAlbumImage.id, this.viewsService.etag, blursData)
			.pipe(takeUntil(this.atComponentDestroy))
			.toPromise().catch(e => console.log(e));

		const newAttachment = await this.externalApiService
			.getAttachmentById(this.order.id, this.currentAlbumImage.id)
			.pipe(takeUntil(this.atComponentDestroy))
			.toPromise().catch(e => console.log(e));

		if (updateAttachment.succeed) {
			setTimeout(async () => {
				this.broadcastService.broadcast({
					name: ToasterType.TOASTER,
					event: {
						type: 'info',
						title: 'Image updated',
						message: 'Image was updated'
					}
				});
				this.saving = false;
				this.loading = false;
				this.onAcceptSuccess(newAttachment);
			}, 2500);
		}

	}

	/**
	 * event when mouse is down
	 * @param event
	 */
	onMouseDown(event: MouseEvent): void {
		event.preventDefault();
		event.stopPropagation();
		this.startX = event.offsetX;
		this.startY = event.offsetY;
		this.isDown = true;
	}

	/**
	 * event when the user move the mouse
	 * @param e
	 */
	onMouseMove(e: MouseEvent): void {
		e.preventDefault();
		e.stopPropagation();
		if (!this.isDown || !this.newBlur) {
			return;
		}

		// calculate current mouse position
		const mouseX = e.offsetX;
		const mouseY = e.offsetY;

		// calculate size of rectangle
		const width = mouseX - this.startX;
		const height = mouseY - this.startY;

		this.drawRectangle();

		this.position.x = this.startX;
		this.position.y = this.startY;
		this.position.width = width;
		this.position.height = height;
	}

	/**
	 * event when the user unclick the pointer
	 * @param event
	 */
	onMouseUp(event: MouseEvent): void {
		event.preventDefault();
		event.stopPropagation();
		this.isDown = false;
	}

	/**
	 * event when the mouse is outside the area
	 * @param event
	 */
	onMouseOut(event: MouseEvent): void {
		event.preventDefault();
		event.stopPropagation();
		this.isDown = false;
	}

	/**
	 * event to draw a shape in the canvas
	 */
	drawRectangle() {
		this.clearCanvas();
		this.ctx.beginPath();
		this.ctx.fillStyle = 'rgba(104,104,104,0.8)';
		this.ctx.shadowColor = 'rgb(104,104,104)';
		this.ctx.strokeStyle = 'white';
		this.ctx.filter = 'blur(5px)';

		this.ctx.drawImage(this.canvas.nativeElement,
			this.position.x, this.position.y, this.position.width, this.position.height,
			this.position.x, this.position.y, this.position.width, this.position.height,
		);

		this.ctx.filter = 'none';

		this.ctx.rect(this.position.x, this.position.y, this.position.width, this.position.height);
		this.ctx.fill();
		this.ctx.stroke();
		this.position = new ShapeCoordinates();
		this.drawShapes();
	}

	/**
	 * event to draw shapes in the list of blurs
	 */
	drawShapes() {
		for (const shape of this.listBlur) {
			this.ctx.beginPath();
			this.ctx.strokeStyle = 'white';

			if (shape.active) {
				this.ctx.strokeStyle = '#3B82F6';
			}

			this.ctx.shadowColor = 'transparent';
			this.ctx.fillStyle = 'transparent';

			if (!shape.visible) {
				this.ctx.fillStyle = 'rgba(104,104,104,0.8)';
				this.ctx.shadowColor = 'rgb(104,104,104)';
				this.ctx.filter = 'blur(5px)';
			}

			this.ctx.drawImage(this.canvas.nativeElement,
				shape.left, shape.top, shape.width, shape.height,
				shape.left, shape.top, shape.width, shape.height,
			);

			this.ctx.filter = 'none';
			this.ctx.rect(shape.left, shape.top, shape.width, shape.height,);
			this.ctx.fill();
			this.ctx.stroke();
		}
	}

	/**
	 * event to close modal
	 */
	onDismiss(): void {
		super.onCancel();
	}

	/**
	 * Close all when the element is destroyed
	 */
	ngOnDestroy(): void {
		this.atComponentDestroy.next({});
		this.atComponentDestroy.unsubscribe();
	}
}
